import akbank from './assets/akbank.png';
import alternatifbank from './assets/alternatifbank.png';
import anadolubank from './assets/anadolubank.png';
import burganbank from './assets/burganbank.png';
import denizbank from './assets/denizbank.png';
import finansbank from './assets/finansbank.png';
import garanti from './assets/garanti.png';
import halkbank from './assets/halkbank.png';
import hsbc from './assets/hsbc.png';
import ing from './assets/ing.png';
import isbank from './assets/isbank.png';
import odeabank from './assets/odeabank.png';
import sekerbank from './assets/sekerbank.png';
import teb from './assets/teb.png';
import vakifbank from './assets/vakifbank.png';
import yapikredi from './assets/yapikredi.png';
import ziraat from './assets/ziraat.png';

/**
 * BANKA KATALOGU — dovizhub-mobile/src/shared/constant/bankData.ts ile AYNI
 * `code` degerleri.
 *
 * Kodlar sunucuya `accountCode` olarak yaziliyor ve mobil istemci ayni kodlari
 * yaziyor. Web'de farkli bir kod uretilseydi ayni kullanicinin telefonda
 * ekledigi hesabin logosu tarayicida cikmazdi (ve tersi).
 *
 * `brandName` BIR CEVIRI ANAHTARI DEGIL — banka ozel adi, hicbir dilde
 * degismez. Alan adi bunu acikca soyluyor: `label` denseydi "neden labelKey
 * degil?" sorusu her okuyusta yeniden sorulurdu (bkz. src/layout/menus.ts,
 * modul seviyesinde t() yasagi). Ad ayrica sunucuya `accountLabel` olarak
 * gonderilir; mobil de oyle yapiyor.
 *
 * LOGOLAR YEREL. Disaridan (banka sitesinden) baglanmak uretimde
 * `default-src 'self'` CSP'si tarafindan SESSIZCE engellenirdi — kirik resim
 * bile gorunmez, sadece bos alan kalir. Dosyalar mobil uygulamadan kopyalandi.
 */

export type BankOption = Readonly<{
  /** Sunucuya yazilan kararli kod (`accountCode`). */
  code: string;
  /** Bankanin ozel adi. Cevrilmez. */
  brandName: string;
}>;

/**
 * Sirala: tr-TR karsilastirmasi. 'İş Bankası' ile 'ING' arasindaki sira
 * Turkce'de Latin varsayilanindan farklidir; localeCompare olmadan liste
 * kullaniciya rastgele gorunur.
 */
export const BANK_LIST: readonly BankOption[] = Object.freeze(
  (
    [
      { code: 'ziraat', brandName: 'Ziraat Bankası' },
      { code: 'halkbank', brandName: 'Halkbank' },
      { code: 'vakifbank', brandName: 'VakıfBank' },
      { code: 'isbank', brandName: 'İş Bankası' },
      { code: 'yapikredi', brandName: 'Yapı Kredi' },
      { code: 'garanti', brandName: 'Garanti BBVA' },
      { code: 'akbank', brandName: 'Akbank' },
      { code: 'finansbank', brandName: 'QNB Finansbank' },
      { code: 'denizbank', brandName: 'DenizBank' },
      { code: 'teb', brandName: 'TEB' },
      { code: 'hsbc', brandName: 'HSBC Türkiye' },
      { code: 'sekerbank', brandName: 'Şekerbank' },
      { code: 'odeabank', brandName: 'Odeabank' },
      { code: 'ing', brandName: 'ING Bank Türkiye' },
      { code: 'alternatifbank', brandName: 'Alternatif Bank' },
      { code: 'anadolubank', brandName: 'Anadolu Bank' },
      { code: 'burganbank', brandName: 'Burgan Bank' },
    ] satisfies BankOption[]
  )
    .slice()
    .sort((a, b) => a.brandName.localeCompare(b.brandName, 'tr'))
    .map((bank) => Object.freeze(bank)),
);

/** Kod -> logo URL'i. Vite dosyayi derlemeye dahil eder ve taban yolunu kendisi kor. */
const BANK_LOGOS: Readonly<Record<string, string>> = Object.freeze({
  akbank,
  alternatifbank,
  anadolubank,
  burganbank,
  denizbank,
  finansbank,
  garanti,
  halkbank,
  hsbc,
  ing,
  isbank,
  odeabank,
  sekerbank,
  teb,
  vakifbank,
  yapikredi,
  ziraat,
});

/**
 * Logo YOKSA `undefined` doner — cagri yeri o zaman bas harfli rozete duser.
 * Bos bir `<img src="">` cizmek yerine bu: sunucuda bilinmeyen bir
 * `accountCode` (eski kayit, elle girilmis satir) kirik resim ikonu uretirdi.
 */
export const bankLogo = (code: string): string | undefined => BANK_LOGOS[code];

const BANK_BY_CODE: ReadonlyMap<string, BankOption> = new Map(
  BANK_LIST.map((bank) => [bank.code, bank]),
);

/**
 * Kod -> gorunen ad. Bilinmeyen kodda SUNUCUDAN GELEN etiket kullanilir
 * (`accountLabel`), o da yoksa kodun kendisi. Hicbir durumda bos donmez:
 * adsiz bir banka karti kullaniciya hangi hesabina baktigini soylemez.
 */
export const bankDisplayName = (code: string, fallbackLabel?: string): string =>
  BANK_BY_CODE.get(code)?.brandName ?? (fallbackLabel || code);

/** Logosu olmayan banka icin rozet metni — adin ilk harfi. */
export const bankInitial = (displayName: string): string => displayName.trim().charAt(0);

/**
 * Hesap acilabilen para birimleri.
 *
 * Mobildeki `currencyFilterList` + TRY. TRY listede cunku takasin karsi bacagi neredeyse
 * her zaman TRY ve kullanicinin bir TRY hesabi olmadan islem tamamlanamiyor.
 *
 * AUD 2026-09-07'de eklendi: `task_type` seed'inde AUD/TRY cifti VARDI (id 4) ama hicbir
 * istemcide hesap kuru olarak secilemiyordu. Sonuc: bir AUD talebi acilsa bile eslesme
 * kurali iki bacagi da isteyip HICBIR buroyu bulamiyordu -- talep hic gorunmuyordu.
 * Bu liste ile task_type seed'i AYNI para birimlerini tasimali.
 */
export const ACCOUNT_CURRENCIES: readonly string[] = Object.freeze([
  'USD',
  'EUR',
  'GBP',
  'CAD',
  'AUD',
  'TRY',
]);

/** TRY kisayolunun kullandigi kod — string tekrarini onlemek icin tek yerde. */
export const TRY_CURRENCY = 'TRY';
